import React, { useEffect, useRef, useState } from 'react';
import './FiltresBarre.css';

// ─── Barre de filtres compacte (2026-10-23) ──────────────────────────────────
// Une barre qui tient sur une ligne, quel que soit le nombre de filtres :
//   · la recherche prend la place libre ;
//   · un ou deux choix du quotidien restent visibles (<Bascule>) ;
//   · le reste vit dans un panneau ouvert par un bouton (<BoutonPanneau>),
//     qui affiche le nombre de filtres actifs ;
//   · chaque filtre actif devient une pastille retirable (<Pastilles>) : on
//     sait toujours pourquoi la liste est plus courte.
// Les filtres s'appliquent dès qu'on les change : pas de bouton « Valider ».
// Première page : OR en cours. À réutiliser tel quel sur les autres pages.

// Bascule segmentée (2 à 4 choix exclusifs).
export function Bascule({ valeur, onChange, options, label }) {
  return (
    <div className="fb-seg" role="group" aria-label={label}>
      {options.map(o => (
        <button key={o.value} type="button" aria-pressed={valeur === o.value}
          onClick={() => onChange(o.value)} title={o.title}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

// Bouton qui ouvre un panneau flottant. Se ferme au clic dehors et sur Échap.
export function BoutonPanneau({ libelle, icone, compte = 0, aligne = 'droite', largeur = 360, children }) {
  const [ouvert, setOuvert] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!ouvert) return undefined;
    const dehors = (e) => { if (ref.current && !ref.current.contains(e.target)) setOuvert(false); };
    const echap = (e) => { if (e.key === 'Escape') setOuvert(false); };
    document.addEventListener('mousedown', dehors);
    document.addEventListener('keydown', echap);
    return () => { document.removeEventListener('mousedown', dehors); document.removeEventListener('keydown', echap); };
  }, [ouvert]);
  return (
    <div className="fb-ancre" ref={ref}>
      <button type="button" className={`fb-btn${ouvert ? ' ouvert' : ''}`} aria-expanded={ouvert} onClick={() => setOuvert(v => !v)}>
        {icone}
        <span className="fb-btn-l">{libelle}</span>
        {compte > 0 && <span className="fb-compte">{compte}</span>}
      </button>
      {ouvert && (
        <div className={`fb-panneau ${aligne === 'gauche' ? 'gauche' : ''}`} style={{ width: `min(${largeur}px, calc(100vw - 32px))` }}>
          {children}
        </div>
      )}
    </div>
  );
}

// Rubrique d'un panneau : libellé + contrôle.
export function Rubrique({ titre, children }) {
  return <div className="fb-rub"><span className="fb-rub-t">{titre}</span>{children}</div>;
}

// Choix en pastilles cliquables (un seul à la fois ; recliquer désélectionne).
export function ChoixPastilles({ valeur, onChange, options }) {
  return (
    <div className="fb-opts">
      {options.map(o => (
        <button key={o.value} type="button" className="fb-opt" aria-pressed={valeur === o.value}
          onClick={() => onChange(valeur === o.value ? '' : o.value)}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

// Choix en pastilles, plusieurs à la fois (`valeurs` : tableau).
export function ChoixMultiples({ valeurs, onChange, options }) {
  return (
    <div className="fb-opts">
      {options.map(o => (
        <button key={o.value} type="button" className="fb-opt" aria-pressed={valeurs.includes(o.value)}
          onClick={() => onChange(valeurs.includes(o.value) ? valeurs.filter(v => v !== o.value) : [...valeurs, o.value])}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

// Interrupteur avec libellé et explication facultative.
export function Interrupteur({ id, checked, onChange, libelle, aide }) {
  return (
    <label className="fb-sw" htmlFor={id}>
      <span>{libelle}{aide && <small>{aide}</small>}</span>
      <input id={id} type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} />
    </label>
  );
}

// Pastilles des filtres actifs. `filtres` : [{ cle, libelle, valeur, retirer }].
export function Pastilles({ filtres, onToutEffacer, resultat }) {
  return (
    <div className="fb-chips" aria-live="polite">
      {filtres.length === 0 && <span className="fb-aucun">Aucun filtre</span>}
      {filtres.map(f => (
        <span key={f.cle} className="fb-chip">
          {f.libelle ? `${f.libelle} : ` : ''}<b>{f.valeur}</b>
          <button type="button" aria-label={`Retirer le filtre ${f.libelle || f.valeur}`} onClick={f.retirer}>✕</button>
        </span>
      ))}
      {filtres.length > 1 && <button type="button" className="fb-lien" onClick={onToutEffacer}>Tout effacer</button>}
      {resultat != null && <span className="fb-res">{resultat}</span>}
    </div>
  );
}

export const IconeFiltres = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><path d="M4 6h16M7 12h10M10 18h4" /></svg>
);
export const IconeCalendrier = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M3 10h18M8 3v4M16 3v4" /></svg>
);
